// Offline / degraded-mode answers, built entirely from the canonical profile data.
// Used only when the AI backend is unreachable, so a visitor still gets useful,
// accurate answers instead of an error. No DOM scraping, no hardcoded facts.

import { ABOUT, SKILLS, PROJECTS, EXPERIENCE, BLOG, CONTACT } from '../data/profile.js';

const STOP = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'do', 'does', 'did', 'to', 'of', 'in',
  'on', 'for', 'with', 'and', 'or', 'me', 'my', 'your', 'his', 'about', 'what', 'whats',
  'tell', 'can', 'you', 'have', 'has', 'i', 'he', 'him', 'please', 'give',
]);

const INTENTS = [
  { key: 'greeting', kws: ['hi', 'hello', 'hey', 'yo', 'greetings', 'howdy', 'sup'] },
  { key: 'education', kws: ['education', 'study', 'studied', 'college', 'university', 'degree', 'iiit', 'gwalior', 'graduate', 'graduated', 'btech', 'mtech', 'thesis', 'academic', 'school'] },
  { key: 'skills', kws: ['skill', 'skills', 'tech', 'stack', 'technology', 'technologies', 'language', 'languages', 'framework', 'frameworks', 'tool', 'tools', 'know', 'expertise'] },
  { key: 'projects', kws: ['project', 'projects', 'built', 'build', 'portfolio', 'github', 'repo', 'repository', 'apps'] },
  { key: 'experience', kws: ['experience', 'job', 'work', 'career', 'intern', 'internship', 'mindtickle', 'alfalgo', 'role', 'company', 'employer'] },
  { key: 'blog', kws: ['blog', 'blogs', 'article', 'articles', 'post', 'posts', 'writing', 'write', 'medium'] },
  { key: 'contact', kws: ['contact', 'email', 'reach', 'hire', 'hiring', 'connect', 'linkedin', 'social', 'touch'] },
  { key: 'about', kws: ['about', 'who', 'background', 'bio', 'yourself', 'shubhajeet', 'person'] },
];

function tokenize(text) {
  return (text.toLowerCase().match(/[a-z0-9+]+/g) || []).filter((t) => !STOP.has(t));
}

function classify(query) {
  const tokens = tokenize(query);
  if (!tokens.length) return null;
  const tokenSet = new Set(tokens);
  let best = null;
  for (const intent of INTENTS) {
    let score = 0;
    for (const kw of intent.kws) if (tokenSet.has(kw)) score += 1;
    if (score > 0 && (!best || score > best.score)) best = { key: intent.key, score };
  }
  return best ? best.key : null;
}

function skillsAnswer() {
  const byCat = {};
  for (const s of SKILLS) (byCat[s.category] = byCat[s.category] || []).push(s.name);
  const lines = Object.entries(byCat).map(([cat, names]) => `- **${cat}:** ${names.join(', ')}`);
  return `Shubhajeet works across the backend, applied ML, and cloud. His toolkit:\n\n${lines.join('\n')}`;
}

function projectsAnswer() {
  const lines = PROJECTS.map((p) => `- **${p.title}** — ${p.desc}${p.link ? ` [link](${p.link})` : ''}`);
  return `A few things Shubhajeet has built:\n\n${lines.join('\n')}`;
}

function experienceAnswer() {
  const lines = EXPERIENCE.map(
    (e) => `- **${e.role}**, ${e.org} (${e.period}) — ${e.desc}`
  );
  return `Here's Shubhajeet's work experience:\n\n${lines.join('\n')}`;
}

function educationAnswer() {
  const e = ABOUT.education;
  return `Shubhajeet completed an **${e.degree}** at **${e.institution}** (graduated ${e.graduated}). His M.Tech thesis was on ${e.thesis}, advised by ${e.advisor}.`;
}

function blogAnswer() {
  const lines = BLOG.map((b) => `- **${b.title}** — ${b.excerpt} [read](${b.link})`);
  return `Shubhajeet has written a few posts:\n\n${lines.join('\n')}`;
}

function contactAnswer() {
  const socials = CONTACT.socials.map((s) => `[${s.label}](${s.href})`).join(' · ');
  return `You can reach Shubhajeet at **${CONTACT.email}**.\n\n${socials}`;
}

function aboutAnswer() {
  return `${ABOUT.bio}\n\nWant to hear about his projects, experience, or skills?`;
}

function greetingAnswer() {
  return `Hi! I'm Monk, ${ABOUT.name}'s assistant. I can tell you about his projects, experience, skills, writing, or how to get in touch. What would you like to know?`;
}

function defaultAnswer() {
  return `I'm here to help with anything about ${ABOUT.name} — his **projects**, **experience**, **skills**, **blog**, or **contact** details. Which would you like to explore?`;
}

const ANSWERS = {
  greeting: greetingAnswer,
  about: aboutAnswer,
  education: educationAnswer,
  skills: skillsAnswer,
  projects: projectsAnswer,
  experience: experienceAnswer,
  blog: blogAnswer,
  contact: contactAnswer,
};

/**
 * Answer a question locally from profile data.
 * @param {string} query
 * @returns {string} a markdown answer
 */
export function answerLocally(query) {
  const intent = classify(query || '');
  const gen = intent && ANSWERS[intent];
  return gen ? gen() : defaultAnswer();
}
